import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Phone, User, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import LandingPage from '@/pages/LandingPage';
import BRAND from '@/brand';
import Wordmark from '@/components/Wordmark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEW_STEPS = ['Info', 'SMS-kode', 'Email-kode', 'Adgangskode'];

function InvitePage() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [stepLoading, setStepLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Ny bruger
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  // Eksisterende bruger — kun login
  const [existingPassword, setExistingPassword] = useState('');
  const [autoTilknytter, setAutoTilknytter] = useState(false);

  // Er den der ser siden allerede logget ind som netop den inviterede?
  const sammeBruger = !!user && !!invite &&
    (user.email || '').toLowerCase() === (invite.email || '').toLowerCase();
  const andenBruger = !!user && !!invite && !sammeBruger;

  useEffect(() => {
    if (!token) { setError('Intet invitation-token fundet'); setLoading(false); return; }
    axios.get(`${API}/invite/${token}`)
      .then(r => {
        setInvite(r.data);
        if (!r.data.user_exists) {
          if (r.data.email_verified) setStep(3);
          else if (r.data.sms_verified) setStep(2);
        }
      })
      .catch(e => setError(e.response?.data?.detail || 'Invitation ikke fundet eller udløbet'))
      .finally(() => setLoading(false));
  }, [token]);

  // Logget ind som den inviterede: tilknyt uden at bede om adgangskoden igen.
  // Adgangen bindes pr. ejer, så en bruger kan samle selskaber fra flere ejere.
  useEffect(() => {
    if (!sammeBruger || autoTilknytter || done) return;
    setAutoTilknytter(true);
    axios.post(`${API}/invite/${token}/accept`)
      .then(async r => {
        await fetchUser();
        toast.success(r.data?.allerede_tilknyttet
          ? 'Du var allerede tilknyttet'
          : `Tilknyttet ${r.data?.owner_name || ''}`.trim());
        navigate('/dashboard');
      })
      .catch(e => {
        setAutoTilknytter(false);
        setError(e.response?.data?.detail || 'Kunne ikke acceptere invitationen');
      });
  }, [sammeBruger, autoTilknytter, done, token, fetchUser, navigate]);

  const completeInvite = async (pwd) => {
    const res = await axios.post(`${API}/invite/${token}/complete`, { password: pwd });
    const accessToken = res.data?.access_token;
    if (accessToken) {
      localStorage.setItem('token', accessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      await fetchUser();
      toast.success(invite.user_exists ? 'Tilknyttet — velkommen!' : 'Konto oprettet — velkommen!');
      navigate('/profil');
    } else {
      setDone(true);
    }
  };

  const nextExisting = async () => {
    setStepLoading(true);
    try {
      await completeInvite(existingPassword);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forkert adgangskode');
    } finally { setStepLoading(false); }
  };

  const next = async () => {
    setStepLoading(true);
    try {
      if (step === 0) {
        await axios.post(`${API}/invite/${token}/send-sms`, { name, phone });
        toast.success('SMS-kode sendt til ' + phone);
        setStep(1);
      } else if (step === 1) {
        await axios.post(`${API}/invite/${token}/verify-sms`, { code: smsCode });
        toast.success('SMS bekræftet — email-kode sendt til ' + invite.email);
        setStep(2);
      } else if (step === 2) {
        await axios.post(`${API}/invite/${token}/verify-email`, { code: emailCode });
        toast.success('Email bekræftet');
        setStep(3);
      } else if (step === 3) {
        if (password !== password2) { toast.error('Adgangskoderne matcher ikke'); return; }
        if (password.length < 8) { toast.error('Mindst 8 tegn'); return; }
        await completeInvite(password);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fejl');
    } finally { setStepLoading(false); }
  };

  const canNext = () => {
    if (step === 0) return name.trim().length > 1 && phone.replace(/\s/g, '').length >= 8;
    if (step === 1) return smsCode.length === 6;
    if (step === 2) return emailCode.length === 6;
    if (step === 3) return password.length >= 8 && password === password2;
    return false;
  };

  const inputClass = "pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted";
  const codeClass = "h-14 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted text-center text-3xl tracking-[12px]";

  return (
    <div className="relative min-h-screen">
      <div className="fixed inset-0 overflow-auto blur-sm brightness-50 pointer-events-none">
        <LandingPage />
      </div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[var(--surface-overlay)]" />
        <Card className="relative z-10 w-full max-w-md p-8 bg-th-sidebar border-th-border shadow-2xl shadow-brand-500/10">
          <div className="flex justify-center mb-6">
            <a href="/" className="flex items-center gap-3">
              <Mail size={40} className="text-brand-500" />
              <span className="text-2xl font-heading font-bold text-th-text">
                <Wordmark />
              </span>
            </a>
          </div>

          {loading ? (
            <p className="text-th-text-secondary text-center">Indlæser invitation...</p>
          ) : error ? (
            <div className="text-center space-y-4">
              <p className="text-red-400">{error}</p>
              <Button className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg" onClick={() => navigate('/login')}>
                Gå til login
              </Button>
            </div>
          ) : done ? (
            <div className="text-center space-y-4">
              <CheckCircle size={48} className="text-green-500 mx-auto" />
              <h1 className="text-2xl font-heading font-bold text-th-text">Tilknyttet!</h1>
              <p className="text-th-text-secondary">Du kan nu logge ind med din eksisterende konto.</p>
              <Button className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg" onClick={() => navigate('/login')}>
                Log ind
              </Button>
            </div>
          ) : sammeBruger ? (
            /* ── Allerede logget ind som den inviterede — tilknyttes nu ── */
            <div className="text-center space-y-4 py-4">
              <h1 className="text-2xl font-heading font-bold text-th-text">Tilknytter...</h1>
              <p className="text-th-text-secondary">
                Du er logget ind som <strong>{user.email}</strong> — invitationen accepteres automatisk.
              </p>
            </div>
          ) : invite?.user_exists ? (
            /* ── Eksisterende bruger — log ind for at acceptere ── */
            <>
              <h1 className="text-2xl font-heading font-bold text-th-text text-center mb-1">
                Accepter invitation
              </h1>
              <p className="text-th-text-secondary text-center mb-6">
                <strong>{invite.owner_name}</strong> har inviteret dig til {BRAND.name}
              </p>
              <div className="bg-th-hover border border-th-border rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-th-text-muted uppercase tracking-wider mb-0.5">Din konto</p>
                <p className="text-th-text font-medium">{invite.email}</p>
                <p className="text-xs text-th-text-muted mt-1">Du har allerede en konto — bekræft med dit eksisterende password for at acceptere invitationen.</p>
              </div>
              {andenBruger && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-4">
                  <p className="text-sm text-amber-500">
                    Du er logget ind som <strong>{user.email}</strong>, men invitationen er sendt til{' '}
                    <strong>{invite.email}</strong>. Skriv adgangskoden til {invite.email} for at tilknytte den konto.
                  </p>
                </div>
              )}
              <div className="relative mb-4">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                <Input autoFocus type="password" value={existingPassword}
                  onChange={e => setExistingPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && existingPassword.length >= 8 && nextExisting()}
                  placeholder="Din adgangskode" className={inputClass} />
              </div>
              <Button
                onClick={nextExisting}
                disabled={stepLoading || existingPassword.length < 8}
                className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg text-lg"
              >
                {stepLoading ? 'Vent...' : 'Accepter invitation'}
              </Button>
            </>
          ) : (
            /* ── Ny bruger — fuldt flow ── */
            <>
              <h1 className="text-2xl font-heading font-bold text-th-text text-center mb-1">
                Accepter invitation
              </h1>
              <p className="text-th-text-secondary text-center mb-6">
                <strong>{invite.owner_name}</strong> har inviteret dig til {BRAND.name}
              </p>

              <div className="flex items-center gap-2 mb-6">
                {NEW_STEPS.map((_, i) => (
                  <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-brand-500' : 'bg-th-hover'}`} />
                ))}
              </div>
              <p className="text-xs text-th-text-muted uppercase tracking-wider mb-4">Trin {step + 1} af {NEW_STEPS.length}</p>

              {step === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-th-text-secondary mb-2">Udfyld dit navn og mobilnummer. Vi sender en SMS-kode for at bekræfte.</p>
                  <div className="relative">
                    <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                    <Input autoFocus value={name} onChange={e => setName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && canNext() && next()}
                      placeholder="Dit fulde navn" className={inputClass} />
                  </div>
                  <div className="relative">
                    <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                    <Input value={phone} onChange={e => setPhone(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && canNext() && next()}
                      placeholder="Mobilnummer" className={inputClass} />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-3">
                  <p className="text-sm text-th-text-secondary mb-2">Indtast den 6-cifrede kode sendt til <strong>{phone}</strong></p>
                  <Input autoFocus value={smsCode} onChange={e => setSmsCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && canNext() && next()}
                    placeholder="------" maxLength={6} className={codeClass} />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-3">
                  <p className="text-sm text-th-text-secondary mb-2">Indtast koden sendt til <strong>{invite.email}</strong></p>
                  <Input autoFocus value={emailCode} onChange={e => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && canNext() && next()}
                    placeholder="------" maxLength={6} className={codeClass} />
                </div>
              )}

              {step === 3 && (
                <div className="space-y-3">
                  <p className="text-sm text-th-text-secondary mb-2">Vælg en adgangskode til din konto (mindst 8 tegn)</p>
                  <div className="relative">
                    <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                    <Input autoFocus type="password" value={password} onChange={e => setPassword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && canNext() && next()}
                      placeholder="Adgangskode" className={inputClass} />
                  </div>
                  <div className="relative">
                    <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                    <Input type="password" value={password2} onChange={e => setPassword2(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && canNext() && next()}
                      placeholder="Gentag adgangskode" className={inputClass} />
                  </div>
                </div>
              )}

              <Button
                onClick={next}
                disabled={stepLoading || !canNext()}
                className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg text-lg mt-6"
              >
                {stepLoading ? 'Vent...' : step === 3 ? 'Opret konto' : 'Næste'}
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export default InvitePage;
